<script setup lang="ts">
    import { ref, computed, watch, watchEffect } from 'vue';
    import { useRoute } from 'vue-router';
    import axios from 'axios';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import IntegrationConfigFields from '@/js/back-office/components/IntegrationConfigFields.vue';
    import { useIntegrationConfigForm } from '@/js/composables/useIntegrationConfigForm';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { GENESYS_SYSTEM_KEY } from '@/js/consts/integration-systems';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetIntegrationConfigOptions,
        useQueryGetIntegrationConfigs,
        useQueryGetIntegrationConfig,
        useMutationSaveIntegrationConfig,
        useMutationDeleteIntegrationConfig,
        useMutationTestGenesysConnection,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const route = useRoute();
    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const { data: systemOptionsData } = useQueryGetIntegrationConfigOptions();

    const genesysSystem = computed(
        () =>
            (systemOptionsData.value ?? []).find((s) => s.systemKey === GENESYS_SYSTEM_KEY) ?? null
    );

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    // Arriving via the overview page's "edit" link preselects the tenant through this query param.
    const initialTenantId = typeof route.query.tenantId === 'string' ? route.query.tenantId : '';
    const selectedTenantId = ref(initialTenantId);
    const tenantSearch = ref<string | null>(null);
    const genesysSystemKey = computed(() => GENESYS_SYSTEM_KEY);

    watchEffect(() => {
        const tenant = tenantOptions.value.find((t) => t.value === selectedTenantId.value);
        if (tenant) {
            tenantSearch.value = tenant.name;
        }
    });

    const { data: configuredSystemsData } = useQueryGetIntegrationConfigs(selectedTenantId);
    const isExisting = computed(
        () => configuredSystemsData.value?.some((s) => s.systemKey === GENESYS_SYSTEM_KEY) ?? false
    );

    const { data: existingConfigData } = useQueryGetIntegrationConfig(
        selectedTenantId,
        genesysSystemKey
    );

    const { formValues, canSubmit } = useIntegrationConfigForm(
        selectedTenantId,
        genesysSystem,
        existingConfigData,
        isExisting
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    const { mutateAsync: saveMutation, isPending: isSaving } = useMutationSaveIntegrationConfig();
    const { mutateAsync: deleteMutation, isPending: isDeleting } =
        useMutationDeleteIntegrationConfig();

    const submit = async () => {
        if (!canSubmit.value) return;
        await saveMutation({
            tenantId: selectedTenantId.value,
            systemKey: GENESYS_SYSTEM_KEY,
            fieldValues: formValues.value,
        });
        successDialogStore.openDialog({ message: __('back-office-integration-config-success') });
    };

    const deleteConfig = async () => {
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('delete'),
            message: __('back-office-integration-config-delete-confirm'),
        });
        if (!confirmed) return;

        await deleteMutation({ tenantId: selectedTenantId.value, systemKey: GENESYS_SYSTEM_KEY });
    };

    const canTestConnection = computed(
        () => !!formValues.value['clientId'] && !!formValues.value['clientSecret']
    );

    const testResult = ref<'idle' | 'success' | 'error'>('idle');
    const testErrorMessage = ref('');
    const { mutateAsync: testConnectionMutation, isPending: isTesting } =
        useMutationTestGenesysConnection();

    // A previous test's result stops being trustworthy the moment the credentials it tested change.
    watch(
        () => [
            formValues.value['region'],
            formValues.value['clientId'],
            formValues.value['clientSecret'],
        ],
        () => {
            testResult.value = 'idle';
        }
    );

    const testConnection = async () => {
        if (!canTestConnection.value) return;
        try {
            await testConnectionMutation({
                region: formValues.value['region'] || null,
                clientId: formValues.value['clientId'],
                clientSecret: formValues.value['clientSecret'],
            });
            testResult.value = 'success';
        } catch (error) {
            testResult.value = 'error';
            testErrorMessage.value =
                axios.isAxiosError<{ detail?: string }>(error) &&
                typeof error.response?.data?.detail === 'string'
                    ? error.response.data.detail
                    : __('error-test-genesys-connection');
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-genesys-tab-configure') }}</h1>
        </template>
        <template #content>
            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="genesys-config-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <form
                    v-if="selectedTenantId && genesysSystem"
                    class="flex flex-col gap-6"
                    @submit.prevent="submit"
                >
                    <IntegrationConfigFields
                        v-model="formValues"
                        id-prefix="genesys-config-field"
                        :fields="genesysSystem.fields"
                        :stored-secrets="existingConfigData?.storedSecrets ?? []"
                    >
                        <template #section-Connection>
                            <div class="flex items-center gap-3">
                                <EchoButton
                                    type="button"
                                    variant="secondary"
                                    :disabled="!canTestConnection"
                                    :loading="isTesting"
                                    @click="testConnection"
                                >
                                    {{ __('back-office-genesys-test-connection') }}
                                </EchoButton>
                                <span
                                    v-if="testResult === 'success'"
                                    class="text-success text-body"
                                >
                                    {{ __('back-office-genesys-test-connection-success') }}
                                </span>
                                <span
                                    v-else-if="testResult === 'error'"
                                    class="text-error text-body"
                                >
                                    {{ testErrorMessage }}
                                </span>
                            </div>
                        </template>
                    </IntegrationConfigFields>

                    <div class="flex justify-between">
                        <EchoButton
                            v-if="isExisting"
                            type="button"
                            variant="secondary"
                            :loading="isDeleting"
                            @click="deleteConfig"
                        >
                            {{ __('delete') }}
                        </EchoButton>
                        <div class="flex gap-3">
                            <EchoButton
                                type="submit"
                                variant="primary"
                                :disabled="!canSubmit"
                                :loading="isSaving"
                            >
                                {{ __('back-office-integration-config-save') }}
                            </EchoButton>
                        </div>
                    </div>
                </form>
            </div>
        </template>
    </SettingsPageLayout>
</template>
